import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
  useRealtimeNotifications,
} from '../hooks/use-notifications'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import {
  Bell,
  CalendarCheck,
  Clock,
  Activity,
  CreditCard,
  Syringe,
  MessageSquare,
} from 'lucide-react'
import type { NotificationType } from '@/types'

interface NotificationCenterProps {
  recipientId: string
}

const notificationIcons: Record<NotificationType, typeof Bell> = {
  booking_confirmation: CalendarCheck,
  check_in_reminder: Clock,
  activity_update: Activity,
  payment_receipt: CreditCard,
  vaccination_expiring: Syringe,
  message: MessageSquare,
}

export function NotificationCenter({ recipientId }: NotificationCenterProps) {
  const navigate = useNavigate()
  const { data: notifications } = useNotifications(recipientId)
  const { data: unreadCount } = useUnreadCount(recipientId)
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead(recipientId)

  useRealtimeNotifications(recipientId)

  const handleNotificationClick = (notification: {
    id: string
    read_at: string | null
    action_url: string | null
  }) => {
    if (!notification.read_at) {
      markRead.mutate(notification.id)
    }
    if (notification.action_url) {
      navigate(notification.action_url)
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {(unreadCount ?? 0) > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {unreadCount! > 99 ? '99+' : unreadCount}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {(unreadCount ?? 0) > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              Mark all as read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[400px]">
          {!notifications || notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => {
                const Icon =
                  notificationIcons[
                    notification.notification_type
                  ] ?? Bell
                const isUnread = !notification.read_at

                return (
                  <button
                    key={notification.id}
                    type="button"
                    className={cn(
                      'flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
                      isUnread && 'bg-muted/30'
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 space-y-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <p
                          className={cn(
                            'text-sm leading-snug',
                            isUnread ? 'font-medium' : 'font-normal'
                          )}
                        >
                          {notification.title}
                        </p>
                        {isUnread && (
                          <Badge className="mt-0.5 h-2 w-2 shrink-0 rounded-full p-0" />
                        )}
                      </div>
                      {notification.body && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
